import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { BackdropLoader, toast } from '@livable/saas-react-components';
import { Button, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { track, events } from 'src/configs/amplitude';
import { useStore } from 'src/stores';
import AttachBillFileModal from 'src/components/shared/AttachBillFileModal';
import { FilePreviewPlain } from 'src/components/shared/FilePreviewModal/plainIndex';
import { FileVisibilitySetting } from './fileVisibilitySetting';
import useStyles from './styles';
export const FileDetails = observer(_ref => {
  var _property$allocations, _property$allocations2, _property$allocations4, _property$allocations5, _property$allocations6, _property$allocations12;
  let onFileVisibilitySettingVisibleChange = _ref.onFileVisibilitySettingVisibleChange;
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const page = 'Apply Billing';
  const currentCompany = store.homeDashboardStore.getCurrentCompany || {};
  const applyBill = currentCompany.billingTab.applyBill || {};
  const billingCycleId = applyBill.billingCycleId;
  const billingTab = currentCompany.billingTab;
  const isStatementSent = applyBill.status === 'TableReadyStatementSent';
  const isAllPropertiesMode = store.homeDashboardStore.selectedPropertyId === 'all';
  const property = isAllPropertiesMode ? store.homeDashboardStore.getCurrentCompanyProperties.find(p => p.id === applyBill.propertyId) || {} : store.homeDashboardStore.getCurrentProperty || {};
  const propertyId = property.id;
  const utility = (_property$allocations = property.allocations) === null || _property$allocations === void 0 ? void 0 : _property$allocations.utilityById.get(billingCycleId + applyBill.utilityId);
  const utilityTable = ((_property$allocations2 = property.allocations) === null || _property$allocations2 === void 0 || (_property$allocations2 = _property$allocations2.billingCycles.get(billingCycleId)) === null || _property$allocations2 === void 0 ? void 0 : _property$allocations2.utilityTables.find(i => i.utilityId === applyBill.utilityId)) || {};
  const disabled = isStatementSent || !utilityTable.isEditable;
  const isBillExtractionEnabled = store.account.featureFlagsStore.getIsBillExtractionEnabled;
  const getFileId = () => {
    var _property$allocations3, _utility$bill, _utility$bill2;
    if ((_property$allocations3 = property.allocations) !== null && _property$allocations3 !== void 0 && (_property$allocations3 = _property$allocations3.tempFileData) !== null && _property$allocations3 !== void 0 && _property$allocations3.fileId) return property.allocations.tempFileData.fileId;
    if (utility !== null && utility !== void 0 && (_utility$bill = utility.bill) !== null && _utility$bill !== void 0 && _utility$bill.redactedFileId) return utility.bill.redactedFileId;
    if (utility !== null && utility !== void 0 && (_utility$bill2 = utility.bill) !== null && _utility$bill2 !== void 0 && _utility$bill2.fileId) return utility.bill.fileId;
    return '';
  };
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFileLoading = _useState2[0],
    setIsFileLoading = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    filePreviewLink = _useState4[0],
    setFilePreviewLink = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    fileName = _useState6[0],
    setFileName = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    fileSize = _useState8[0],
    setFileSize = _useState8[1];
  const fileInputRef = useRef(null);
  const postedBillAutomationFileId = useRef('');
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    isFileDragOver = _useState0[0],
    setIsFileDragOver = _useState0[1];

  // ── A bill recovered for this cycle (LB-5822) ────────────────────────────────────────────────────
  // Its document lives in bill-automation, not the file service, so there is no fileId to preview by.

  const recoveredBill = (_property$allocations4 = (_property$allocations5 = property.allocations) === null || _property$allocations5 === void 0 || (_property$allocations5 = _property$allocations5.tempFileData) === null || _property$allocations5 === void 0 ? void 0 : _property$allocations5.billAutomationResponse) !== null && _property$allocations4 !== void 0 ? _property$allocations4 : null;
  const hasRecoveredBill = !getFileId() && !!(recoveredBill !== null && recoveredBill !== void 0 && recoveredBill.billId) && !((_property$allocations6 = property.allocations) !== null && _property$allocations6 !== void 0 && (_property$allocations6 = _property$allocations6.tempFileData) !== null && _property$allocations6 !== void 0 && _property$allocations6.isDeleted);
  const _useState1 = useState({
      status: 'loading',
      url: ''
    }),
    _useState10 = _slicedToArray(_useState1, 2),
    recoveredPreview = _useState10[0],
    setRecoveredPreview = _useState10[1];
  useEffect(() => {
    if (!hasRecoveredBill) {
      setRecoveredPreview({
        status: 'loading',
        url: ''
      });
      return undefined;
    }
    let active = true;
    setRecoveredPreview({
      status: 'loading',
      url: ''
    });
    (async _resp$response => {
      // Fetched on open, never cached: the URL is presigned and expires in about fifteen minutes.
      const resp = await store.companyBills.getBillFileUrlAsync.run({
        billId: recoveredBill.billId
      });
      if (!active) return;
      const url = resp && resp.isSuccessful !== false ? (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.url : '';
      setRecoveredPreview(url ? {
        status: 'ready',
        url
      } : {
        status: 'none',
        url: ''
      });
    })();
    return () => {
      active = false;
    };
  }, [hasRecoveredBill, recoveredBill === null || recoveredBill === void 0 ? void 0 : recoveredBill.billId, store.companyBills]);
  const isFileVisibilitySettingVisible = Boolean(getFileId() && filePreviewLink);
  useEffect(() => {
    onFileVisibilitySettingVisibleChange === null || onFileVisibilitySettingVisibleChange === void 0 || onFileVisibilitySettingVisibleChange(isFileVisibilitySettingVisible);
    return () => {
      onFileVisibilitySettingVisibleChange === null || onFileVisibilitySettingVisibleChange === void 0 || onFileVisibilitySettingVisibleChange(false);
    };
  }, [isFileVisibilitySettingVisible, onFileVisibilitySettingVisibleChange]);
  const getFileInfo = async data => {
    setIsFileLoading(true);
    const resp = await store.files.getFileInfoAsync.run(data);
    return resp;
  };
  const getFileBlobForBillAutomation = async fileId => {
    if (!fileId) return null;
    if (store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled) {
      const filePreviewLinkResp = await store.files.getPresignedFileDownloadUrlAsync.run({
        fileId,
        isPreview: false
      });
      if (filePreviewLinkResp !== null && filePreviewLinkResp !== void 0 && filePreviewLinkResp.isSuccessful && filePreviewLinkResp.response) {
        const fileResp = await fetch(filePreviewLinkResp.response);
        if (fileResp.ok) return fileResp.blob();
      }
      // Fallback to direct preview link when presigned URL is unavailable
    }
    const previewLink = store.files.getFilePreviewLink({
      fileId
    });
    const fileResp = await fetch(previewLink);
    if (!fileResp.ok) return null;
    return fileResp.blob();
  };
  const postBillForAutomation = async response => {
    var _response$response, _property$allocations7, _response$response2, _responseFileName$spl, _property$buildingInf, _property$dashboard;
    if (!store.account.featureFlagsStore.getIsBillExtractionEnabled) return;
    const fileId = response === null || response === void 0 || (_response$response = response.response) === null || _response$response === void 0 ? void 0 : _response$response.fileId;
    if (!fileId || !propertyId) return;
    if (postedBillAutomationFileId.current === fileId) return;

    // A new file replaces the last one's extraction right away. Until this file's own result arrives, the
    // previous document's validated response would otherwise stay live behind the new filename — with its
    // Push button enabled and its bill id the one that gets pushed — and it would stay that way for good if
    // the start-bill call below failed.
    (_property$allocations7 = property.allocations) === null || _property$allocations7 === void 0 || _property$allocations7.setTempFileData('billAutomationResponse', null);
    const blob = await getFileBlobForBillAutomation(fileId);
    if (!blob) return;
    const responseFileName = (response === null || response === void 0 || (_response$response2 = response.response) === null || _response$response2 === void 0 ? void 0 : _response$response2.fileName) || 'bill-upload';
    const extension = (_responseFileName$spl = responseFileName.split('.').pop()) === null || _responseFileName$spl === void 0 ? void 0 : _responseFileName$spl.toLowerCase();
    const extensionToMime = {
      pdf: 'application/pdf',
      jpeg: 'image/jpeg',
      jpg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp'
    };
    const allowedMimes = Object.values(extensionToMime);
    const mimeType = blob.type && allowedMimes.includes(blob.type) ? blob.type : extensionToMime[extension] || 'application/pdf';
    const file = new File([blob], responseFileName, {
      type: mimeType
    });
    const utilityInfo = store.utilityProviders.utilities.get(applyBill.utilityId) || {};
    const propertyAddress = applyBill.buildingAddress || ((_property$buildingInf = property.buildingInformation) === null || _property$buildingInf === void 0 ? void 0 : _property$buildingInf.buildingAddress) || ((_property$dashboard = property.dashboard) === null || _property$dashboard === void 0 ? void 0 : _property$dashboard.propertyAddress) || '';
    const automationResp = await store.billAutomationBillsApi.startBillAsync.run({
      file,
      propertyId,
      utilityNames: utilityInfo.providerName || undefined,
      propertyAddress: propertyAddress || undefined,
      requiredUtilities: utilityInfo.utilityTypeName || undefined,
      // What this upload is for, so the bill can be recovered while it is still being read — and
      // still if reading it fails, which resolves nothing to file it under.
      billingCycleId: billingCycleId || undefined,
      utilityId: applyBill.utilityId || undefined
    });
    if (automationResp !== null && automationResp !== void 0 && automationResp.isSuccessful && automationResp !== null && automationResp !== void 0 && automationResp.response) {
      var _property$allocations8, _property$allocations9;
      postedBillAutomationFileId.current = fileId;
      (_property$allocations8 = property.allocations) === null || _property$allocations8 === void 0 || _property$allocations8.setTempFileData('billAutomationResponse', automationResp.response);
      // Cleared alongside: this bill was uploaded just now, so nothing about it was left unfinished.
      // Only the recovery sets the flag, and only a reset has cleared it until now — which today is
      // enough, because a bill on screen hides the upload area. It stops being enough the moment
      // uploading over a recovered bill is allowed, and the banner would then say something untrue.
      (_property$allocations9 = property.allocations) === null || _property$allocations9 === void 0 || _property$allocations9.setTempFileData('wasRecovered', null);
    }
  };
  const fileCallback = async response => {
    if (response) {
      var _response$response3;
      setFileName(response.response.fileName);
      setFileSize((_response$response3 = response.response) === null || _response$response3 === void 0 ? void 0 : _response$response3.fileSizeMB);
      await postBillForAutomation(response).catch(() => {});
      if (store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled) {
        var _response$response4;
        const fileNamePartials = (_response$response4 = response.response) === null || _response$response4 === void 0 ? void 0 : _response$response4.fileName.split('.');
        if (['doc', 'docx'].includes(fileNamePartials[fileNamePartials.length - 1])) {
          const requestFilePreviewLinkResp = await store.files.getRequestPreviewLinkAsync.run({
            fileId: response.response.fileId
          });
          if (requestFilePreviewLinkResp !== null && requestFilePreviewLinkResp !== void 0 && requestFilePreviewLinkResp.isSuccessful) {
            setFilePreviewLink(requestFilePreviewLinkResp.response);
          }
        } else {
          const filePreviewLinkResp = await store.files.getPresignedFileDownloadUrlAsync.run({
            fileId: response.response.fileId,
            isPreview: true
          });
          if (filePreviewLinkResp !== null && filePreviewLinkResp !== void 0 && filePreviewLinkResp.isSuccessful) {
            setFilePreviewLink(filePreviewLinkResp.response);
          }
        }
      } else {
        var _response$response5;
        setFilePreviewLink(store.files.getFilePreviewLink({
          fileId: response.response.fileId
        }, false, ".".concat((_response$response5 = response.response) === null || _response$response5 === void 0 || (_response$response5 = _response$response5.fileName) === null || _response$response5 === void 0 || (_response$response5 = _response$response5.split('.').pop()) === null || _response$response5 === void 0 ? void 0 : _response$response5.toLowerCase())));
      }
    }
    if (response === null) {
      setFileName('');
      setFilePreviewLink('');
    }
    setIsFileLoading(false);
  };
  useEffect(() => {
    if (getFileId()) getFileInfo({
      fileId: getFileId()
    }).then(fileCallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utility]);
  useEffect(() => {
    if (currentCompany.billingTab.isFileChosen) {
      currentCompany.billingTab.setIsFileChosen(false);
      currentCompany.billingTab.setIsPreviewCycleChargesModalOpen(false);
      getFileInfo({
        fileId: getFileId()
      }).then(fileCallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany.billingTab.isFileChosen]);
  const addToPreviewQueue = () => {
    var _store$homeDashboardS;
    return billingTab.addToPreviewQueue({
      billingCycleId,
      id: billingCycleId + propertyId,
      portfolioId: ((_store$homeDashboardS = store.homeDashboardStore.getCurrentPortfolio) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.id) || '',
      propertyId
    });
  };
  const deleteFile = async () => {
    var _utility$bill3;
    track(events.fileRemoved('Bill'), {
      page,
      utility: applyBill.utilityName
    });
    await store.files.deleteFileAsync.run({
      fileId: getFileId()
    });
    if (utility !== null && utility !== void 0 && (_utility$bill3 = utility.bill) !== null && _utility$bill3 !== void 0 && _utility$bill3.id) {
      var _utility$bill4;
      await store.allocations.getFileAttachedAsync.run({
        fileId: null,
        isShared: false,
        billId: (_utility$bill4 = utility.bill) === null || _utility$bill4 === void 0 ? void 0 : _utility$bill4.id
      }).then(addToPreviewQueue).then(() => {
        var _utility$bill5, _utility$bill6, _utility$bill7, _property$allocations0, _property$allocations1;
        utility === null || utility === void 0 || (_utility$bill5 = utility.bill) === null || _utility$bill5 === void 0 || _utility$bill5.changeRedactedFileId(null);
        utility === null || utility === void 0 || (_utility$bill6 = utility.bill) === null || _utility$bill6 === void 0 || _utility$bill6.changeIsSharedFile(false);
        utility === null || utility === void 0 || (_utility$bill7 = utility.bill) === null || _utility$bill7 === void 0 || _utility$bill7.changeFileId(null);
        (_property$allocations0 = property.allocations) === null || _property$allocations0 === void 0 || (_property$allocations0 = _property$allocations0.billingCycles.get(billingCycleId)) === null || _property$allocations0 === void 0 || (_property$allocations0 = _property$allocations0.utilityTables.find(i => i.utilityId === applyBill.utilityId)) === null || _property$allocations0 === void 0 || _property$allocations0.changeRedactedFileId(null);
        (_property$allocations1 = property.allocations) === null || _property$allocations1 === void 0 || (_property$allocations1 = _property$allocations1.billingCycles.get(billingCycleId)) === null || _property$allocations1 === void 0 || (_property$allocations1 = _property$allocations1.utilityTables.find(i => i.utilityId === applyBill.utilityId)) === null || _property$allocations1 === void 0 || _property$allocations1.changeFileId(null);
      });
    }
    property.allocations.setTempFileData('fileId', '');
    property.allocations.setTempFileData('isDeleted', true);
    property.allocations.setTempFileData('isReducted', false);
    property.allocations.setTempFileData('billAutomationResponse', null);
    fileCallback(null);
  };
  const uploadFile = async id => {
    var _utility$bill8;
    property.allocations.setTempFileData('fileId', id);
    property.allocations.setTempFileData('isDeleted', false);
    property.allocations.setTempFileData('isReducted', false);
    if (utility !== null && utility !== void 0 && (_utility$bill8 = utility.bill) !== null && _utility$bill8 !== void 0 && _utility$bill8.id) {
      var _property$allocations10, _utility$bill9;
      await store.allocations.getFileAttachedAsync.run({
        fileId: id,
        isShared: (_property$allocations10 = property.allocations) === null || _property$allocations10 === void 0 || (_property$allocations10 = _property$allocations10.tempFileData) === null || _property$allocations10 === void 0 ? void 0 : _property$allocations10.isShared,
        billId: (_utility$bill9 = utility.bill) === null || _utility$bill9 === void 0 ? void 0 : _utility$bill9.id
      }).then(() => {
        var _utility$bill0, _property$allocations11;
        utility === null || utility === void 0 || (_utility$bill0 = utility.bill) === null || _utility$bill0 === void 0 || _utility$bill0.changeFileId(id);
        (_property$allocations11 = property.allocations) === null || _property$allocations11 === void 0 || (_property$allocations11 = _property$allocations11.billingCycles.get(billingCycleId)) === null || _property$allocations11 === void 0 || (_property$allocations11 = _property$allocations11.utilityTables.find(i => i.utilityId === applyBill.utilityId)) === null || _property$allocations11 === void 0 || _property$allocations11.changeFileId(id);
      }).then(addToPreviewQueue);
    }
    await store.files.getFilesInfoAsync.run({
      fileIds: [id]
    });
    await getFileInfo({
      fileId: id
    }).then(fileCallback);
  };
  const dropFileUploaderCallback = async id => {
    if (id === null) {
      await deleteFile();
    } else {
      await uploadFile(id);
    }
  };
  const dropFileUploaderCallbackRef = useRef(dropFileUploaderCallback);
  dropFileUploaderCallbackRef.current = dropFileUploaderCallback;
  const processBillFile = useCallback(async function (pickedFile) {
    let _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      resetFileInput = _ref2.resetFileInput,
      source = _ref2.source;
    if (!pickedFile) return;
    if (pickedFile.size >= 1048576 * 100) {
      toast('error', {
        body: ['File is too large, maximum file size is 100 MB']
      });
      resetFileInput === null || resetFileInput === void 0 || resetFileInput();
      return;
    }
    setIsFileLoading(true);
    try {
      var _uploadedFileResult;
      let uploadedFileResult;
      if (store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled) {
        uploadedFileResult = await store.files.uploadFileViaPresignedUrlExperimentAsync.run({
          file: pickedFile,
          createdBy: store.account.userProfile.id
        });
      } else {
        uploadedFileResult = await store.files.uploadFileAsync.run({
          file: pickedFile
        });
      }
      if ((_uploadedFileResult = uploadedFileResult) !== null && _uploadedFileResult !== void 0 && _uploadedFileResult.isSuccessful) {
        track(events.fileUploaded('Bill'), {
          page,
          source
        });
        await dropFileUploaderCallbackRef.current(uploadedFileResult.response);
      } else {
        setIsFileLoading(false);
      }
    } catch (_unused) {
      setIsFileLoading(false);
    } finally {
      resetFileInput === null || resetFileInput === void 0 || resetFileInput();
    }
  }, [page, store.account.featureFlagsStore, store.account.userProfile.id, store.files]);
  const handleDirectFileUpload = async event => {
    var _event$target;
    const pickedFile = event === null || event === void 0 || (_event$target = event.target) === null || _event$target === void 0 || (_event$target = _event$target.files) === null || _event$target === void 0 ? void 0 : _event$target[0];
    if (!pickedFile) return;
    const input = event === null || event === void 0 ? void 0 : event.target;
    await processBillFile(pickedFile, {
      resetFileInput: () => {
        if (input) input.value = '';
      },
      source: 'File Picker'
    });
  };
  const hasFileDrag = event => {
    var _event$dataTransfer;
    const types = event === null || event === void 0 || (_event$dataTransfer = event.dataTransfer) === null || _event$dataTransfer === void 0 ? void 0 : _event$dataTransfer.types;
    if (!types) return false;
    return Array.from(types).includes('Files');
  };
  const handleDropAreaDragEnter = useCallback(e => {
    if (disabled || !hasFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    // Ignore re-enters when moving between children inside the drop zone.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsFileDragOver(true);
  }, [disabled]);
  const handleDropAreaDragLeave = useCallback(e => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsFileDragOver(false);
  }, [disabled]);
  const handleDropAreaDragOver = useCallback(e => {
    if (disabled || !hasFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = 'copy';
    } catch (_unused2) {
      /* ignore */
    }
  }, [disabled]);
  const handleDropAreaDrop = useCallback(async e => {
    var _e$dataTransfer;
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragOver(false);
    const file = (_e$dataTransfer = e.dataTransfer) === null || _e$dataTransfer === void 0 || (_e$dataTransfer = _e$dataTransfer.files) === null || _e$dataTransfer === void 0 ? void 0 : _e$dataTransfer[0];
    if (!file) return;
    await processBillFile(file, {
      source: 'Drag And Drop'
    });
  }, [disabled, processBillFile]);
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.modalEditor, " ").concat(!(getFileId() === '' || ((_property$allocations12 = property.allocations) === null || _property$allocations12 === void 0 || (_property$allocations12 = _property$allocations12.tempFileData) === null || _property$allocations12 === void 0 ? void 0 : _property$allocations12.fileId) === '') ? 'isFile' : '')
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: !!isFileLoading
  }), getFileId() === '' && !hasRecoveredBill ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.active, " ").concat(classes.dropArea, " ").concat(disabled ? 'disabled' : '', " ").concat(isFileDragOver ? classes.dropAreaFileDragOver : ''),
    onDragEnter: handleDropAreaDragEnter,
    onDragLeave: handleDropAreaDragLeave,
    onDragOver: handleDropAreaDragOver,
    onDrop: handleDropAreaDrop
  }, /*#__PURE__*/React.createElement(Typography, {
    component: "p",
    variant: "h4"
  }, isBillExtractionEnabled ? 'Upload your bill file and we will extract data for you' : 'Upload your bill file'), /*#__PURE__*/React.createElement(Button, {
    'custom-attribute': '07-attach-file',
    className: classes.attachFileButton,
    disabled: disabled,
    onClick: () => {
      var _fileInputRef$current;
      track(events.clicked('Attach Bill File'), {
        page,
        utility: applyBill.utilityName
      });
      (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 || _fileInputRef$current.click();
    },
    size: "small",
    variant: "contained"
  }, "Attach bill file"), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    onChange: handleDirectFileUpload,
    style: {
      display: 'none'
    }
  }), /*#__PURE__*/React.createElement(Typography, {
    className: classes.attachFileControllersDivider,
    variant: "body2"
  }, t('or')), /*#__PURE__*/React.createElement("span", {
    'custom-attribute': '07-attach-file',
    className: classes.attachFileLinkButton,
    disabled: disabled,
    onClick: () => {
      track(events.modalOpened('Use Previous Bills'), {
        page,
        utility: applyBill.utilityName
      });
      currentCompany.billingTab.setIsAttachBillFileModalOpen(applyBill.utilityName);
    },
    size: "small",
    variant: "text"
  }, t('allocations.use_previous_bills'))), /*#__PURE__*/React.createElement(AttachBillFileModal, {
    disabled: disabled,
    dropFileUploaderCallback: dropFileUploaderCallback,
    property: property,
    setIsFileLoading: setIsFileLoading,
    utilityId: applyBill.utilityId
  })) : null, hasRecoveredBill ? /*#__PURE__*/React.createElement("div", {
    className: classes.recoveredFilePane
  }, recoveredPreview.status === 'ready' ?
  /*#__PURE__*/
  // Read in-page by the same viewer every other document here uses. Deliberately NOT an
  // iframe: the browser would render the PDF with its built-in viewer, which is a
  // force-installed extension, and on a managed profile whose DeveloperToolsAvailability
  // policy is left at its default that blocks DevTools for the whole tab. This needs the
  // bill bucket to allow the origin, which it now does (LB-5822).
  React.createElement(FilePreviewPlain, {
    docUri: recoveredPreview.url,
    fileId: "",
    fileName: (recoveredBill === null || recoveredBill === void 0 ? void 0 : recoveredBill.fileName) || 'Bill document',
    isDisabledParent: true,
    isPreviewOnly: true,
    key: recoveredPreview.url
  }) : /*#__PURE__*/React.createElement("div", {
    className: classes.recoveredFilePlaceholder
  }, recoveredPreview.status === 'none' ? 'No document is available to preview for this bill.' : 'Loading the bill document…')) : null, getFileId() && filePreviewLink && /*#__PURE__*/React.createElement(FilePreviewPlain, {
    handleDelete: async () => {
      await deleteFile();
    },
    key: getFileId() + filePreviewLink + Date.now(),
    isPreviewForm: true,
    docUri: filePreviewLink,
    fileName: fileName,
    fileSize: fileSize,
    isDisabledParent: false,
    fileId: getFileId(),
    isPreviewOnly: isStatementSent,
    uploadEditedFile: async newId => {
      setFilePreviewLink('');
      await uploadFile(newId);
    }
  }), getFileId() && filePreviewLink && /*#__PURE__*/React.createElement(FileVisibilitySetting, null));
});